import { useState, useEffect, useRef } from "react";

/**
 * Connectivity pill.
 *
 * Losing signal is a state, not a failure — so this is a quiet floating pill
 * rather than a red bar across the top of someone's day. It says what happens
 * to their words, which is the only thing they actually need to know.
 *
 * It shares the top anchor with the toast stack, so while it is showing it
 * publishes its own height as `--net-offset` and the toasts sit underneath it
 * rather than on top of it — which matters, because the likeliest moment to
 * fire a toast ("saved — will sync") is exactly while this pill is up.
 *
 * ── Announcement ──────────────────────────────────────────────────────────
 * Losing signal used to be completely silent for a screen-reader user: the
 * whole component returned null while online, so the live region and its text
 * were inserted into the DOM in the same commit, and a region created together
 * with its content is generally not announced. CrisisSupport already documents
 * and solves this; the fix simply hadn't been applied here.
 *
 * So the live region is now permanently mounted and empty, and the sentence is
 * written into it a beat later. The visible pill is unaffected — it still
 * appears immediately, and is aria-hidden so the same words aren't announced
 * twice.
 */
export default function OfflineBanner({ isOnline, pendingSyncs }) {
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const pillRef = useRef(null);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline) {
      setShowReconnected(true);
      const timer = setTimeout(() => {
        setShowReconnected(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  const visible = !isOnline || showReconnected;

  // Presentation only: reserve room at the shared top anchor so the toast stack
  // stacks below this pill instead of landing on top of it.
  //
  // Measured, not guessed. A hard-coded height was wrong by ~7px at one line
  // and catastrophically wrong at two — on a 360px screen the offline string
  // wraps and the toast landed on top of the very pill the offset exists to
  // avoid. The observer keeps the offset true through wrapping, font scaling
  // and the pending count appearing.
  useEffect(() => {
    const root = document.documentElement;
    const el = pillRef.current;
    if (!visible || !el) {
      root.style.removeProperty("--net-offset");
      return undefined;
    }
    const publish = () => {
      const h = el.offsetHeight || 0;
      root.style.setProperty("--net-offset", `${Math.round(h) + 8}px`);
    };
    publish();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    return () => {
      if (ro) ro.disconnect();
      root.style.removeProperty("--net-offset");
    };
  }, [visible, isOnline, pendingSyncs]);

  // Mount the live region empty, then write into it 150ms later. Same pattern,
  // and same reason, as CrisisSupport: a region inserted in the same tick as
  // its content is usually not announced at all.
  const spoken = !isOnline
    ? `Offline. Anything you save is kept here and syncs later.${pendingSyncs > 0 ? ` ${pendingSyncs} waiting.` : ""}`
    : pendingSyncs > 0
      ? "Back online. Catching up now."
      : "Back online.";

  useEffect(() => {
    if (!visible) {
      setAnnouncement("");
      return undefined;
    }
    const t = setTimeout(() => setAnnouncement(spoken), 150);
    return () => clearTimeout(t);
  }, [visible, spoken]);

  return (
    <>
      <p className="sr-only" role="status" aria-live="polite">{announcement}</p>

      {visible && (
        <div
          className={`shell-net ${isOnline ? "shell-net--online" : ""}`}
          aria-hidden="true"
        >
          <p className="shell-net-pill" ref={pillRef}>
            {!isOnline ? (
              <>
                <span className="shell-net-icon" aria-hidden="true">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12.5a10 10 0 0 1 14 0" />
                    <path d="M8.6 16.1a5 5 0 0 1 6.8 0" />
                    <line x1="12" y1="19.8" x2="12.01" y2="19.8" />
                    <line x1="3.5" y1="3.5" x2="20.5" y2="20.5" />
                  </svg>
                </span>
                <span>Offline — saved here, syncs later</span>
                {pendingSyncs > 0 && (
                  <span className="shell-net-count">· {pendingSyncs} waiting</span>
                )}
              </>
            ) : (
              <>
                <span className="shell-net-icon" aria-hidden="true">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span>{pendingSyncs > 0 ? "Back online — catching up" : "Back online"}</span>
              </>
            )}
          </p>
        </div>
      )}
    </>
  );
}
